// src/models/paymentModel.js - Frontend Payment Model
import api from '../services/api';




// Payment Management Functions

// Get all payments with filters
export const getAllPayments = async (filters = {}) => {
    try {
        const response = await api.get('/payments', { params: filters });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching payments' };
    }
};

// Get payment by ID
export const getPayment = async (id) => {
    try {
        const response = await api.get(`/payments/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching payment' };
    }
};

// Create new payment
export const createPayment = async (paymentData) => {
    try {
        const response = await api.post('/payments', paymentData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error creating payment' };
    }
};

// Update payment
export const updatePayment = async (id, paymentData) => {
    try {
        const response = await api.put(`/payments/${id}`, paymentData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error updating payment' };
    }
};

// Delete payment
export const deletePayment = async (id) => {
    try {
        const response = await api.delete(`/payments/${id}`);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error deleting payment' };
    }
};

// Record an owner payment (stored in owner_installments)
export const createOwnerPayment = async (ownerId, paymentData) => {
    try {
        const response = await api.post(`/owners/${ownerId}/payments`, paymentData);
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error recording owner payment' };
    }
};

// Get payment statistics
export const getPaymentStats = async (period = 'all') => {
    try {
        const response = await api.get('/payments/stats', { params: { period } });
        return response.data;
    } catch (error) {
        throw error.response?.data || { success: false, message: 'Error fetching payment stats' };
    }
};

// Helper Functions

// Format payment date
export const formatPaymentDate = (dateString) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    }).format(date);
};

// Format currency
export const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
    }).format(amount || 0);
};

// Get payment type color
export const getPaymentTypeColor = (paymentType, isDarkMode = false) => {
    const colors = {
        'rent': isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-100 text-red-800',
        'service': isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-100 text-green-800',
        'commission': isDarkMode ? 'bg-purple-900 bg-opacity-20 text-purple-400' : 'bg-purple-100 text-purple-800',
        'insurance': isDarkMode ? 'bg-yellow-900 bg-opacity-20 text-yellow-400' : 'bg-yellow-100 text-yellow-800'
    };

    return colors[paymentType] || (isDarkMode ? 'bg-zinc-950 bg-opacity-20 text-zinc-400' : 'bg-gray-100 text-gray-800');
};

// Validate payment data
export const validatePaymentData = (paymentData) => {
    const errors = {};

    if (!paymentData.amount || paymentData.amount <= 0) {
        errors.amount = 'Amount must be greater than 0';
    }

    if (!paymentData.paymentDate) {
        errors.paymentDate = 'Payment date is required';
    }

    if (!paymentData.tenantId) {
        errors.tenantId = 'Tenant is required';
    }

    if (!paymentData.months || !Array.isArray(paymentData.months) || paymentData.months.length === 0) {
        errors.months = 'At least one month must be selected';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
};

// Payment type options
export const PAYMENT_TYPE_OPTIONS = [
    { value: 'rent', label: 'Rent' },
    { value: 'service', label: 'Service Fee' },
    { value: 'commission', label: 'Office Commission' },
    { value: 'insurance', label: 'Insurance' }
];

// Payment period options
export const PAYMENT_PERIOD_OPTIONS = [
    { value: 'all', label: 'All Time' },
    { value: 'year', label: 'This Year' },
    { value: 'month', label: 'This Month' }
];

export default {
    getAllPayments,
    getPayment,
    createPayment,
    createOwnerPayment,
    updatePayment,
    deletePayment,
    getPaymentStats,
    formatPaymentDate,
    formatCurrency,
    getPaymentTypeColor,
    validatePaymentData,
    PAYMENT_TYPE_OPTIONS,
    PAYMENT_PERIOD_OPTIONS
};